import * as THREE from 'three'
import type {
  GraphicsBackend,
  GraphicsCapability,
  GraphicsCaptureRect,
  GraphicsClearPolicy,
  GraphicsLifecycleState,
  GraphicsRegionCaptureRequest,
  GraphicsRegionCaptureResult,
  GraphicsRegionCaptureSession,
  GraphicsResizeRequest,
  GraphicsResolutionLease,
  GraphicsStateOwner,
  GraphicsStateToken,
  GraphicsSurfaceInfo,
  ReadonlyGraphicsInfo,
} from '@/graphics/GraphicsBackend'
import {
  BackendMismatchError,
  GraphicsCaptureSupersededError,
  GraphicsSubmissionError,
} from '@/graphics/GraphicsErrors'
import { GraphicsResolutionOwner } from '@/graphics/GraphicsResolutionOwner'
import { getLoggerManager } from '@/shared/logging'
import type { RendererViewportRect, ViewRect } from '@/shared/types/view'
import { toRendererViewportRect } from '@/shared/utils/viewRect'

const log = getLoggerManager().getLogger('graphics:webgl2')

interface WebGL2StateSnapshot {
  readonly owner: GraphicsStateOwner
  readonly renderTarget: THREE.WebGLRenderTarget | null
  readonly viewport: THREE.Vector4 | null
  readonly scissor: THREE.Vector4 | null
  readonly scissorTest: boolean
  readonly autoClear: boolean
}

/** WebGL2 backend 构造参数。 */
export interface WebGL2BackendOptions {
  readonly renderer: THREE.WebGLRenderer
  readonly container: HTMLElement
  /** 兼容旧 EngineConfig.pixelRatio 的精确 DPR 覆盖。 */
  readonly pixelRatio?: number
  readonly maxPixelRatio?: number
  readonly devicePixelRatio?: number
  /** 默认 true；false 仅供借用外部 canvas 的受控场景。 */
  readonly ownsCanvas?: boolean
  readonly onContextLost?: (event: Event) => void
  readonly onError?: (error: Error, phase: string) => void
}

/** WebGL 专属诊断，不进入通用 GraphicsBackend API。 */
export interface WebGL2Diagnostics {
  readonly contextLost: boolean
  readonly geometries: number
  readonly textures: number
  readonly programs: number
}

const WEBGL2_CAPABILITIES: GraphicsCapability = Object.freeze({
  surfaceRender: true,
  multiView: true,
  readback: true,
  offscreenTarget: true,
  postprocess: true,
  screenshot: true,
  webglDiagnostics: true,
})

function normalizePositive(value: number | undefined, fallback: number): number {
  return value !== undefined && Number.isFinite(value) && value > 0 ? value : fallback
}

/** 包装现有 Three.js WebGLRenderer 的最小引擎图形后端。 */
export class WebGL2Backend implements GraphicsBackend {
  readonly kind = 'webgl2' as const
  readonly canvas: HTMLCanvasElement
  readonly capabilities = WEBGL2_CAPABILITIES

  private readonly renderer: THREE.WebGLRenderer
  private readonly container: HTMLElement
  private readonly ownsCanvas: boolean
  private readonly maxPixelRatio: number
  private readonly exactPixelRatio?: number
  private readonly devicePixelRatioOverride?: number
  private readonly onContextLost?: (event: Event) => void
  private readonly onError?: (error: Error, phase: string) => void
  private readonly stateSnapshots = new WeakMap<object, WebGL2StateSnapshot>()
  private readonly deferredCleanups = new Set<() => void>()
  private readonly resolutionOwner: GraphicsResolutionOwner
  private lifecycleState: GraphicsLifecycleState = 'active'
  /** backend-owned 区域采集期间的目标；pipeline 提交 null 目标时由它接管。 */
  private captureTarget: THREE.WebGLRenderTarget | null = null
  private surfaceInfo: GraphicsSurfaceInfo
  private currentGeneration = 0
  private frameOpen = false
  private activeView: ViewRect | null = null
  private initialized = false

  private readonly handleContextLost = (event: Event): void => {
    event.preventDefault?.()
    if (this.lifecycleState !== 'active') {
      return
    }
    this.lifecycleState = 'lost'
    this.frameOpen = false
    this.activeView = null
    this.flushDeferredCleanups()
    log.warn(`event=webgl.context.lost generation=${this.currentGeneration}`)
    this.onContextLost?.(event)
  }

  constructor(options: WebGL2BackendOptions) {
    this.renderer = options.renderer
    this.container = options.container
    this.canvas = options.renderer.domElement
    this.ownsCanvas = options.ownsCanvas ?? true
    this.maxPixelRatio = normalizePositive(options.maxPixelRatio, Number.POSITIVE_INFINITY)
    this.exactPixelRatio = options.pixelRatio
    this.devicePixelRatioOverride = options.devicePixelRatio
    this.onContextLost = options.onContextLost
    this.onError = options.onError
    this.surfaceInfo = this.createSurfaceInfo(1, 1, 1)
    this.resolutionOwner = new GraphicsResolutionOwner(
      () => this.surfaceInfo.pixelRatio,
      (pixelRatio) =>
        this.applyResolvedResize(
          this.surfaceInfo.logicalWidth,
          this.surfaceInfo.logicalHeight,
          pixelRatio
        )
    )

    if (!this.canvas.isConnected || this.canvas.parentElement !== this.container) {
      this.container.appendChild(this.canvas)
    }
    this.container.style.outline = 'none'
    this.canvas.style.outline = 'none'
    this.canvas.addEventListener('webglcontextlost', this.handleContextLost)
    this.resize({
      width: this.container.clientWidth,
      height: this.container.clientHeight,
      pixelRatio: this.exactPixelRatio,
      devicePixelRatio: this.resolveDevicePixelRatio(),
    })
  }

  get surface(): GraphicsSurfaceInfo {
    return this.surfaceInfo
  }

  get state(): GraphicsLifecycleState {
    return this.lifecycleState
  }

  get canSubmit(): boolean {
    return this.lifecycleState === 'active'
  }

  get generation(): number {
    return this.currentGeneration
  }

  acquireResolutionLease(owner: GraphicsStateOwner): GraphicsResolutionLease {
    this.assertCanSubmit()
    return this.resolutionOwner.acquire(owner)
  }

  resize(request: GraphicsResizeRequest): boolean {
    this.assertCanSubmit()
    const logicalWidth = Math.max(Math.round(request.width), 1)
    const logicalHeight = Math.max(Math.round(request.height), 1)
    const requestedDevicePixelRatio = normalizePositive(
      request.devicePixelRatio,
      this.resolveDevicePixelRatio()
    )
    const pixelRatio =
      this.resolutionOwner.overridePixelRatio ??
      normalizePositive(
        request.pixelRatio ?? this.exactPixelRatio,
        Math.min(this.maxPixelRatio, requestedDevicePixelRatio)
      )
    return this.applyResolvedResize(logicalWidth, logicalHeight, pixelRatio)
  }

  private applyResolvedResize(
    logicalWidth: number,
    logicalHeight: number,
    pixelRatio: number
  ): boolean {
    const next = this.createSurfaceInfo(logicalWidth, logicalHeight, pixelRatio)
    const previous = this.surfaceInfo
    const unchanged =
      this.initialized &&
      previous.logicalWidth === next.logicalWidth &&
      previous.logicalHeight === next.logicalHeight &&
      previous.drawingBufferWidth === next.drawingBufferWidth &&
      previous.drawingBufferHeight === next.drawingBufferHeight &&
      previous.pixelRatio === next.pixelRatio &&
      previous.clientRect.x === next.clientRect.x &&
      previous.clientRect.y === next.clientRect.y &&
      previous.clientRect.width === next.clientRect.width &&
      previous.clientRect.height === next.clientRect.height

    this.surfaceInfo = next
    if (unchanged) {
      return false
    }

    this.renderer.setPixelRatio(pixelRatio)
    this.renderer.setSize(logicalWidth, logicalHeight, true)
    this.initialized = true
    return true
  }

  beginFrame(): number {
    this.assertCanSubmit()
    if (this.frameOpen) {
      throw new GraphicsSubmissionError('当前 WebGL2 frame 尚未结束', this.currentGeneration)
    }
    this.currentGeneration++
    this.frameOpen = true
    return this.currentGeneration
  }

  endFrame(generation: number): void {
    if (!this.frameOpen) {
      if (!this.canSubmit) {
        this.flushDeferredCleanups()
        return
      }
      throw new GraphicsSubmissionError('当前没有可结束的 WebGL2 frame', generation)
    }
    if (generation !== this.currentGeneration) {
      throw new GraphicsSubmissionError(
        `frame generation 已失效：expected=${this.currentGeneration}, actual=${generation}`,
        generation
      )
    }
    this.activeView = null
    this.frameOpen = false
    this.flushDeferredCleanups()
  }

  deferUntilFrameEnd(cleanup: () => void, generation = this.currentGeneration): void {
    this.assertCanSubmit(generation)
    if (!this.frameOpen) {
      throw new GraphicsSubmissionError('只能在已开始的 frame 内登记延迟清理', generation)
    }
    this.deferredCleanups.add(cleanup)
  }

  assertCanSubmit(generation?: number): void {
    if (!this.canSubmit) {
      throw new GraphicsSubmissionError(
        `WebGL2 backend 处于 ${this.lifecycleState} 状态，禁止提交`,
        generation
      )
    }
    if (generation !== undefined && generation !== this.currentGeneration) {
      throw new GraphicsSubmissionError(
        `frame generation 已失效：expected=${this.currentGeneration}, actual=${generation}`,
        generation
      )
    }
  }

  captureState(owner: GraphicsStateOwner = this): GraphicsStateToken {
    this.assertCanSubmit()
    const renderer = this.renderer
    const token = Object.freeze({
      backend: this.kind,
      generation: this.currentGeneration,
    }) as GraphicsStateToken
    this.stateSnapshots.set(token, {
      owner,
      renderTarget: renderer.getRenderTarget(),
      viewport:
        typeof renderer.getViewport === 'function'
          ? renderer.getViewport(new THREE.Vector4())
          : null,
      scissor:
        typeof renderer.getScissor === 'function' ? renderer.getScissor(new THREE.Vector4()) : null,
      scissorTest:
        typeof renderer.getScissorTest === 'function' ? renderer.getScissorTest() : false,
      autoClear: renderer.autoClear,
    })
    return token
  }

  restoreState(token: GraphicsStateToken, owner: GraphicsStateOwner = this): void {
    const snapshot = this.stateSnapshots.get(token)
    if (!snapshot) {
      throw new BackendMismatchError(this.kind, token.backend, 'restoreState token owner')
    }
    if (snapshot.owner !== owner) {
      throw new GraphicsSubmissionError('GraphicsStateToken 的 owner 不匹配', token.generation)
    }

    this.renderer.setRenderTarget(snapshot.renderTarget)
    if (snapshot.viewport) {
      this.renderer.setViewport(
        snapshot.viewport.x,
        snapshot.viewport.y,
        snapshot.viewport.z,
        snapshot.viewport.w
      )
    }
    if (snapshot.scissor) {
      this.renderer.setScissor(
        snapshot.scissor.x,
        snapshot.scissor.y,
        snapshot.scissor.z,
        snapshot.scissor.w
      )
    }
    this.renderer.setScissorTest(snapshot.scissorTest)
    this.renderer.autoClear = snapshot.autoClear
  }

  beginView(rect: ViewRect): void {
    this.assertCanSubmit(this.currentGeneration)
    if (!this.frameOpen) {
      throw new GraphicsSubmissionError('beginView 必须位于 beginFrame/endFrame 边界内')
    }
    if (this.activeView) {
      throw new GraphicsSubmissionError('上一 WebGL2 View 尚未结束', this.currentGeneration)
    }
    const viewport = toRendererViewportRect(rect, {
      width: this.surfaceInfo.logicalWidth,
      height: this.surfaceInfo.logicalHeight,
    })
    this.renderer.setViewport(viewport.x, viewport.y, viewport.width, viewport.height)
    this.renderer.setScissor(viewport.x, viewport.y, viewport.width, viewport.height)
    this.renderer.setScissorTest(true)
    this.activeView = Object.freeze({ ...rect })
  }

  clearView(policy: GraphicsClearPolicy = {}): void {
    this.assertCanSubmit(this.currentGeneration)
    if (!this.activeView) {
      throw new GraphicsSubmissionError('clearView 必须位于 beginView/endView 边界内')
    }
    if (policy.clearColor !== undefined) {
      this.renderer.setClearColor(policy.clearColor, policy.clearAlpha)
    } else if (policy.clearAlpha !== undefined) {
      this.renderer.setClearAlpha(policy.clearAlpha)
    }
    this.renderer.clear(policy.color ?? true, policy.depth ?? true, policy.stencil ?? true)
  }

  renderScene(scene: THREE.Scene, camera: THREE.Camera): void {
    this.assertCanSubmit(this.currentGeneration)
    if (!this.frameOpen || !this.activeView) {
      throw new GraphicsSubmissionError('renderScene 必须位于有效 frame/View 边界内')
    }
    const savedAutoClear = this.renderer.autoClear
    this.renderer.autoClear = false
    try {
      this.renderer.render(scene, camera)
    } finally {
      this.renderer.autoClear = savedAutoClear
    }
  }

  endView(): void {
    this.activeView = null
  }

  getInfo(): ReadonlyGraphicsInfo {
    return Object.freeze({
      kind: this.kind,
      state: this.lifecycleState,
      canSubmit: this.canSubmit,
      generation: this.currentGeneration,
      surface: this.surfaceInfo,
      capabilities: this.capabilities,
    })
  }

  /** 返回实际 WebGLRenderer；仅 WebGL2 专属兼容路径可调用。 */
  getRenderer(): THREE.WebGLRenderer {
    return this.renderer
  }

  /**
   * 为存量 WebGL RenderPipeline 应用 render target 与已转换的 bottom-left viewport。
   * 此方法不进入通用 GraphicsBackend；WebGPU 路径必须通过自身公开 renderer primitive。
   */
  applyLegacyPipelineState(
    outputTarget: THREE.WebGLRenderTarget | null,
    viewport: RendererViewportRect
  ): void {
    this.assertCanSubmit(this.currentGeneration)
    // 区域采集期间，调用方传入的 null「默认 surface」应解析为 backend 自有 target。
    this.renderer.setRenderTarget(outputTarget ?? this.captureTarget)
    this.renderer.setViewport(viewport.x, viewport.y, viewport.width, viewport.height)
    this.renderer.setScissor(viewport.x, viewport.y, viewport.width, viewport.height)
    this.renderer.setScissorTest(true)
    this.renderer.autoClear = false
  }

  /** 读取 WebGL 专属诊断，不把 renderer.info/getContext 泄漏到通用 backend。 */
  getWebGLDiagnostics(): WebGL2Diagnostics {
    let contextLost = this.lifecycleState === 'lost'
    try {
      contextLost ||= this.renderer.getContext().isContextLost()
    } catch {
      // backend lifecycle 已提供权威诊断；底层 context 查询失败不改变该状态。
    }
    return Object.freeze({
      contextLost,
      geometries: this.renderer.info?.memory?.geometries ?? 0,
      textures: this.renderer.info?.memory?.textures ?? 0,
      programs: this.renderer.info?.programs?.length ?? 0,
    })
  }

  /**
   * 创建 backend-owned 区域采集 session。
   *
   * 复用同一个离屏 target（尺寸变化时重建），渲染回调由调用方提供；读回统一做 bottom-left → top-left
   * 行翻转，返回 RGBA8。newest-wins：新请求发起后，尚未完成的旧请求以结构化错误结束。
   */
  /** 区域采集期间 pipeline 的默认 surface 提交实际落在 backend-owned target 上。 */
  isOffscreenSubmit(): boolean {
    return this.captureTarget !== null
  }

  /**
   * 区域采集期间的真实提交目标。
   *
   * 仅供 RenderPipeline 构造 legacy `outputTarget` 视图使用：贡献者仍然只读
   * `PhaseRenderContext.outputTarget`，不接触 backend 内部状态。
   * @internal
   */
  getLegacyCaptureTarget(): THREE.WebGLRenderTarget | null {
    return this.captureTarget
  }

  createRegionCaptureSession(): GraphicsRegionCaptureSession {
    return new WebGL2RegionCaptureSession(
      this.renderer,
      () => this.lifecycleState === 'active',
      (target) => {
        this.captureTarget = target
      }
    )
  }

  dispose(): void {
    if (this.lifecycleState === 'disposed' || this.lifecycleState === 'disposing') {
      return
    }
    this.lifecycleState = 'disposing'
    this.resolutionOwner.dispose()
    this.frameOpen = false
    this.activeView = null
    this.flushDeferredCleanups()
    this.canvas.removeEventListener('webglcontextlost', this.handleContextLost)
    this.runDisposePhase('renderer.dispose', () => this.renderer.dispose())
    this.runDisposePhase('renderer.forceContextLoss', () => this.renderer.forceContextLoss?.())
    if (this.ownsCanvas) {
      this.runDisposePhase('canvas.remove', () => this.canvas.remove())
    }
    this.lifecycleState = 'disposed'
  }

  private resolveDevicePixelRatio(): number {
    if (this.devicePixelRatioOverride !== undefined) {
      return normalizePositive(this.devicePixelRatioOverride, 1)
    }
    return normalizePositive(globalThis.devicePixelRatio, 1)
  }

  private createSurfaceInfo(
    logicalWidth: number,
    logicalHeight: number,
    pixelRatio: number
  ): GraphicsSurfaceInfo {
    const rect = this.container.getBoundingClientRect()
    const clientWidth = rect.width > 0 ? rect.width : logicalWidth
    const clientHeight = rect.height > 0 ? rect.height : logicalHeight
    return Object.freeze({
      logicalWidth,
      logicalHeight,
      drawingBufferWidth: Math.max(Math.floor(logicalWidth * pixelRatio), 1),
      drawingBufferHeight: Math.max(Math.floor(logicalHeight * pixelRatio), 1),
      pixelRatio,
      clientRect: Object.freeze({
        x: rect.x ?? rect.left ?? 0,
        y: rect.y ?? rect.top ?? 0,
        width: clientWidth,
        height: clientHeight,
      }),
    })
  }

  private flushDeferredCleanups(): void {
    const cleanups = Array.from(this.deferredCleanups)
    this.deferredCleanups.clear()
    for (const cleanup of cleanups) {
      try {
        cleanup()
      } catch (error) {
        this.reportError(error, 'frame.deferredCleanup')
      }
    }
  }

  private runDisposePhase(phase: string, action: () => void): void {
    try {
      action()
    } catch (error) {
      this.reportError(error, phase)
    }
  }

  private reportError(error: unknown, phase: string): void {
    const normalized = error instanceof Error ? error : new Error(String(error))
    if (this.onError) {
      this.onError(normalized, phase)
      return
    }
    log.error(`event=graphics.webgl2.error phase=${phase} errorName=${normalized.name}`)
  }
}

/** 把请求的采集区域收敛到 target 范围内；未指定时读回整个 target。 */
function resolveCaptureReadRect(
  rect: GraphicsCaptureRect | undefined,
  totalWidth: number,
  totalHeight: number
): GraphicsCaptureRect {
  if (!rect) {
    return { x: 0, y: 0, width: totalWidth, height: totalHeight }
  }
  const width = Math.max(1, Math.min(Math.floor(rect.width), totalWidth))
  const height = Math.max(1, Math.min(Math.floor(rect.height), totalHeight))
  const x = Math.min(Math.max(0, Math.floor(rect.x)), totalWidth - width)
  const y = Math.min(Math.max(0, Math.floor(rect.y)), totalHeight - height)
  return { x, y, width, height }
}

/**
 * WebGL2 的 backend-owned 区域采集 session。
 *
 * 只暴露尺寸与像素结果；调用方拿不到 target、renderer 或 context。
 */
class WebGL2RegionCaptureSession implements GraphicsRegionCaptureSession {
  private target: THREE.WebGLRenderTarget | null = null
  private disposed = false
  private pendingGeneration = 0
  private completedGeneration = 0

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    private readonly isActive: () => boolean,
    private readonly setCaptureTarget: (target: THREE.WebGLRenderTarget | null) => void
  ) {}

  async capture(request: GraphicsRegionCaptureRequest): Promise<GraphicsRegionCaptureResult> {
    if (this.disposed || !this.isActive()) {
      throw new GraphicsSubmissionError('区域采集 session 已不可用')
    }
    const width = Math.max(1, Math.floor(request.width))
    const height = Math.max(1, Math.floor(request.height))
    const generation = ++this.pendingGeneration

    const target = this.ensureTarget(width, height)
    const savedTarget = this.renderer.getRenderTarget()
    request.beforeRender?.()
    try {
      // 让 pipeline 的「默认 surface」提交落到自有 target 上。
      this.setCaptureTarget(target)
      this.renderer.setRenderTarget(target)
      this.renderer.clear(true, true, true)
      await request.render({ width, height })
    } finally {
      this.setCaptureTarget(null)
      request.afterRender?.()
      this.renderer.setRenderTarget(savedTarget)
    }

    if (this.disposed) {
      throw new GraphicsSubmissionError('区域采集 session 已不可用')
    }
    if (generation !== this.pendingGeneration) {
      throw new GraphicsCaptureSupersededError()
    }

    const rect = resolveCaptureReadRect(request.readRect, width, height)
    const pixels = new Uint8Array(rect.width * rect.height * 4)
    // WebGL 读回是 bottom-left 原点，读取 y 需从底部换算。
    const readY = height - rect.y - rect.height
    this.renderer.readRenderTargetPixels(target, rect.x, readY, rect.width, rect.height, pixels)
    this.completedGeneration = generation

    // WebGL 读回是 bottom-left 原点：翻转成 top-left 再返回。
    const flipped = new Uint8Array(pixels.length)
    const rowBytes = rect.width * 4
    for (let row = 0; row < rect.height; row += 1) {
      const src = row * rowBytes
      const dst = (rect.height - row - 1) * rowBytes
      flipped.set(pixels.subarray(src, src + rowBytes), dst)
    }
    return { width: rect.width, height: rect.height, pixels: flipped }
  }

  /** 已完成的请求序号，便于调用方忽略过期结果。 */
  get completedRequestGeneration(): number {
    return this.completedGeneration
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.pendingGeneration += 1
    this.target?.dispose()
    this.target = null
  }

  private ensureTarget(width: number, height: number): THREE.WebGLRenderTarget {
    if (this.target && this.target.width === width && this.target.height === height) {
      return this.target
    }
    this.target?.dispose()
    this.target = new THREE.WebGLRenderTarget(width, height, {
      depthBuffer: true,
      stencilBuffer: true,
    })
    return this.target
  }
}
